import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Clock, MessageSquare, CheckCheck, CheckCircle2, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { api } from "@/lib/api";

const POLL_MS = 20000;
const MUTE_KEY = "boreal.notif.muted";

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
    if (s < 60) return `hace ${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `hace ${m} min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `hace ${h} h`;
    const dd = Math.floor(h / 24);
    return `hace ${dd} d`;
}

function iconFor(kind) {
    if (kind === "whatsapp_inbound") return MessageSquare;
    if (kind === "task_completed") return CheckCircle2;
    if (kind === "task_reopened") return RotateCcw;
    if (kind?.startsWith("timeclock_")) return Clock;
    return Bell;
}

// Reproduce un aviso suave de 2 notas usando Web Audio API (sin archivo externo).
function playSoftChime() {
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const now = ctx.currentTime;
        const master = ctx.createGain();
        master.gain.value = 0.0001;
        master.connect(ctx.destination);
        // Envelope suave global
        master.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
        master.gain.exponentialRampToValueAtTime(0.0001, now + 0.9);

        const tone = (freq, start, dur) => {
            const osc = ctx.createOscillator();
            const g = ctx.createGain();
            osc.type = "sine";
            osc.frequency.value = freq;
            g.gain.value = 0.0001;
            g.gain.exponentialRampToValueAtTime(0.6, now + start + 0.02);
            g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
            osc.connect(g).connect(master);
            osc.start(now + start);
            osc.stop(now + start + dur + 0.05);
        };
        // Do agudo (C5) → Mi (E5): campanilla amable
        tone(523.25, 0, 0.35);
        tone(659.25, 0.16, 0.5);
        setTimeout(() => { try { ctx.close(); } catch (_e) { /* noop */ } }, 1100);
    } catch (_e) { /* silencioso */ }
}

export default function NotificationsBell() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const [muted, setMuted] = useState(() => {
        try { return localStorage.getItem(MUTE_KEY) === "1"; } catch (_e) { return false; }
    });
    const wrapRef = useRef(null);
    const knownIdsRef = useRef(null); // Set con IDs ya vistos por el cliente
    const firstLoadRef = useRef(true);
    const nav = useNavigate();

    const load = useCallback(async () => {
        try {
            const r = await api.get("/notifications");
            const arr = Array.isArray(r.data) ? r.data : [];
            // Detectar entradas nuevas de WhatsApp para reproducir sonido
            const currentIds = new Set(arr.map((n) => n.id));
            if (firstLoadRef.current) {
                knownIdsRef.current = currentIds;
                firstLoadRef.current = false;
            } else if (knownIdsRef.current) {
                const newWhats = arr.filter(
                    (n) => n.kind === "whatsapp_inbound" && !knownIdsRef.current.has(n.id)
                );
                if (newWhats.length > 0) {
                    if (!muted) playSoftChime();
                }
                knownIdsRef.current = currentIds;
            }
            setItems(arr);
        } catch (_e) { /* silencioso */ }
    }, [muted]);

    useEffect(() => {
        load();
        const t = setInterval(load, POLL_MS);
        const onFocus = () => load();
        window.addEventListener("focus", onFocus);
        return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
    }, [load]);

    useEffect(() => {
        const onClick = (e) => {
            if (open && wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    const unread = items.filter((n) => !n.read).length;

    const toggleMute = (e) => {
        e.stopPropagation();
        const next = !muted;
        setMuted(next);
        try { localStorage.setItem(MUTE_KEY, next ? "1" : "0"); } catch (_e) { /* noop */ }
        // Al desmutear, dar feedback audible inmediato
        if (!next) playSoftChime();
    };

    const markAllRead = async () => {
        try {
            await api.post("/notifications/mark-all-read");
            setItems((xs) => xs.map((n) => ({ ...n, read: true })));
        } catch (_e) { /* noop */ }
    };

    const openItem = async (n) => {
        // Marca la notificación como leída (una sola vez).
        try {
            if (!n.read) {
                await api.post(`/notifications/${n.id}/read`);
                setItems((xs) => xs.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
            }
        } catch (_e) { /* noop */ }
        // Si la notificación tiene link (p.ej. seguimiento de coordinación), navegar
        // directamente sin necesidad de "Abrir →". Para el resto, seguimos con toggle.
        const link = n.link || (n.kind === "whatsapp_inbound" ? "/mensajeria" : null);
        if (link) {
            setOpen(false);
            setExpandedId(null);
            nav(link);
            return;
        }
        setExpandedId((prev) => (prev === n.id ? null : n.id));
    };

    const goToLink = async (n, e) => {
        e && e.stopPropagation();
        setOpen(false);
        setExpandedId(null);
        const link = n.link || (n.kind === "whatsapp_inbound" ? "/mensajeria" : null);
        if (link) nav(link);
    };

    return (
        <div className="relative" ref={wrapRef}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="relative inline-flex items-center justify-center w-9 h-9 rounded-md hover:bg-muted transition-colors"
                aria-label="Notificaciones"
                data-testid="notifications-bell"
            >
                <Bell size={18} strokeWidth={1.75} />
                {unread > 0 && (
                    <span
                        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center"
                        data-testid="notifications-badge"
                    >
                        {unread > 99 ? "99+" : unread}
                    </span>
                )}
            </button>

            {open && (
                <div
                    className="absolute right-0 mt-2 w-[340px] max-h-[70vh] overflow-hidden bg-white border border-border rounded-lg shadow-lg z-50 flex flex-col"
                    data-testid="notifications-dropdown"
                >
                    <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                        <div className="text-sm font-semibold">Notificaciones</div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={toggleMute}
                                className="inline-flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:bg-muted"
                                aria-label={muted ? "Activar sonido" : "Silenciar"}
                                title={muted ? "Activar sonido" : "Silenciar"}
                                data-testid="notif-sound-toggle"
                            >
                                {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                            </button>
                            {unread > 0 && (
                                <button
                                    type="button"
                                    onClick={markAllRead}
                                    className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                                    data-testid="mark-all-read"
                                >
                                    <CheckCheck size={12} /> Marcar todas
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="overflow-y-auto flex-1">
                        {items.length === 0 && (
                            <div className="px-4 py-8 text-center text-xs text-muted-foreground">
                                Sin notificaciones
                            </div>
                        )}
                        {items.map((n) => {
                            const Icon = iconFor(n.kind);
                            const text = n.body || n.message || "";
                            const title = n.title || (n.kind === "whatsapp_inbound" ? "WhatsApp entrante" : "Notificación");
                            const isExpanded = expandedId === n.id;
                            const link = n.link || (n.kind === "whatsapp_inbound" ? "/mensajeria" : null);
                            return (
                                <div
                                    key={n.id}
                                    onClick={() => openItem(n)}
                                    className={`w-full text-left px-3 py-2 border-b border-border/60 hover:bg-muted flex gap-2 cursor-pointer ${!n.read ? "bg-primary/5" : ""}`}
                                    data-testid={`notif-item-${n.id}`}
                                >
                                    <div className={`mt-0.5 shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${n.kind === "whatsapp_inbound" ? "bg-green-100 text-green-700" : "bg-primary/10 text-primary"}`}>
                                        <Icon size={12} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="text-[13px] font-medium truncate">{title}</div>
                                            {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />}
                                        </div>
                                        <div className={`text-xs text-muted-foreground ${isExpanded ? "whitespace-pre-wrap break-words" : "truncate"}`}>{text}</div>
                                        <div className="flex items-center justify-between mt-0.5">
                                            <div className="text-[10px] text-muted-foreground/80">{timeAgo(n.created_at)}</div>
                                            {isExpanded && link && (
                                                <button onClick={(e) => goToLink(n, e)}
                                                    className="text-[11px] text-primary hover:underline"
                                                    data-testid={`notif-open-link-${n.id}`}>
                                                    Abrir →
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
